import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Mail, RefreshCw, Inbox, Loader2, Info, AlertCircle, Settings, CheckCircle, XCircle, PlugZap, Link2, Pencil, Sparkles, Trash2, X, Paperclip } from 'lucide-react';
import { extrajudicialAPI } from '../api';
import { toast } from '../hooks/use-toast';

const CLASIF = {
  acuse_recibo: { label: 'Acuse de recibo', cls: 'bg-gray-100 text-gray-700' },
  acepta: { label: 'Acepta', cls: 'bg-green-100 text-green-700' },
  rechaza: { label: 'Rechaza', cls: 'bg-red-100 text-red-700' },
  contraoferta: { label: 'Contraoferta', cls: 'bg-teal-100 text-teal-700' },
  solicita_documentacion: { label: 'Solicita documentación', cls: 'bg-amber-100 text-amber-700' },
  otro: { label: 'Otro', cls: 'bg-slate-100 text-slate-700' },
};
const CLASIF_KEYS = Object.keys(CLASIF);

const RespuestasBancoIMAP = ({ reclamacionId, onCambio }) => {
  const [respuestas, setRespuestas] = useState([]);
  const [modelos, setModelos] = useState({});
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  const [revisando, setRevisando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [estadoImap, setEstadoImap] = useState(null);
  const [probando, setProbando] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [reanalId, setReanalId] = useState(null);
  const [bandejaOpen, setBandejaOpen] = useState(false);
  const [filtro, setFiltro] = useState('dias');
  const [cargandoBandeja, setCargandoBandeja] = useState(false);
  const [correos, setCorreos] = useState([]);
  const [vincForm, setVincForm] = useState(null);
  const [vinculando, setVinculando] = useState(false);
  const [abriendoAdj, setAbriendoAdj] = useState(null);

  useEffect(() => { if (reclamacionId) load(); /* eslint-disable-next-line */ }, [reclamacionId]);

  const load = async () => {
    try {
      const [resp, prov] = await Promise.all([
        extrajudicialAPI.respuestasList(reclamacionId),
        extrajudicialAPI.iaProviders().catch(() => ({ data: null })),
      ]);
      setRespuestas(resp.data || []);
      if (prov && prov.data) {
        setModelos(prov.data.modelos || {});
        setProvider(prov.data.default_provider || '');
        setModel(prov.data.default_model || '');
      }
    } catch (e) { /* noop */ }
  };

  const onProviderChange = (p) => { setProvider(p); const l = modelos[p] || []; setModel(l[0] || ''); };
  const err = (e, def) => (e && e.response && e.response.data && e.response.data.detail) || def;

  const probarConexion = async () => {
    setProbando(true);
    try {
      const r = await extrajudicialAPI.imapEstado();
      setEstadoImap(r.data);
      toast({ title: r.data.ok ? 'Conexión IMAP correcta' : 'Fallo de conexión IMAP', description: r.data.mensaje, variant: r.data.ok ? 'default' : 'destructive' });
    } catch (e) { setEstadoImap({ ok: false, mensaje: err(e, 'No se pudo comprobar la conexión') }); }
    finally { setProbando(false); }
  };

  const revisar = async () => {
    setRevisando(true);
    try {
      const r = await extrajudicialAPI.respuestasRevisar(reclamacionId, { provider, model });
      toast({ title: 'Revisión completada', description: `${r.data.nuevos} nueva(s) de ${r.data.encontrados} email(s) (ref ${r.data.referencia}).` });
      await load();
      if (r.data.nuevos > 0 && typeof onCambio === 'function') onCambio();
    } catch (e) { toast({ title: 'Error', description: err(e, 'No se pudo revisar'), variant: 'destructive' }); }
    finally { setRevisando(false); }
  };

  const sincronizar = async () => {
    setSincronizando(true);
    try {
      const r = await extrajudicialAPI.respuestasSincronizar({ provider, model, dias: 30 });
      toast({ title: 'Bandeja sincronizada', description: `${r.data.respuestas_nuevas} nuevas en ${r.data.reclamaciones_actualizadas} reclamaciones.` });
      await load();
      if (typeof onCambio === 'function') onCambio();
    } catch (e) { toast({ title: 'Error', description: err(e, 'No se pudo sincronizar'), variant: 'destructive' }); }
    finally { setSincronizando(false); }
  };

  const abrirBandeja = async () => { setBandejaOpen(true); await cargarBandeja(); };
  const cargarBandeja = async () => {
    setCargandoBandeja(true); setVincForm(null);
    try {
      const r = await extrajudicialAPI.bandejaImap({ filtro, dias: 30 });
      setCorreos(r.data || []);
    } catch (e) { toast({ title: 'Error', description: err(e, 'No se pudo cargar la bandeja'), variant: 'destructive' }); }
    finally { setCargandoBandeja(false); }
  };

  const abrirAdjunto = async (uid, a) => {
    const key = `${uid}-${a.index}`;
    setAbriendoAdj(key);
    try {
      const r = await extrajudicialAPI.bandejaAdjunto({ uid, index: a.index });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: a.content_type || 'application/octet-stream' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast({ title: 'Error', description: err(e, 'No se pudo abrir el adjunto'), variant: 'destructive' }); }
    finally { setAbriendoAdj(null); }
  };

  const iniciarVinculo = (idx) => setVincForm({ idx, clasificacion: 'otro', importe: '', plazo: '', resumen: '', accion: '', usar_ia: false });
  const confirmarVinculo = async () => {
    if (!vincForm) return;
    const m = correos[vincForm.idx];
    setVinculando(true);
    try {
      await extrajudicialAPI.respuestaVincular(reclamacionId, {
        message_id: m.message_id, remitente: m.remitente, asunto: m.asunto, fecha: m.fecha, cuerpo: m.cuerpo,
        usar_ia: vincForm.usar_ia, provider, model,
        clasificacion: vincForm.clasificacion,
        importe_ofrecido: vincForm.importe !== '' ? parseFloat(vincForm.importe) : null,
        plazo: vincForm.plazo || null, resumen: vincForm.resumen || null, accion_sugerida: vincForm.accion || null,
      });
      toast({ title: 'Correo vinculado', description: vincForm.usar_ia ? 'Clasificado con IA.' : 'Guardado con tu clasificación.' });
      setVincForm(null); setBandejaOpen(false);
      await load();
      if (typeof onCambio === 'function') onCambio();
    } catch (e) { toast({ title: 'Error', description: err(e, 'No se pudo vincular'), variant: 'destructive' }); }
    finally { setVinculando(false); }
  };

  const iniciarEdicion = (r) => {
    setEditId(r.id);
    setEditForm({ clasificacion: r.clasificacion || 'otro', importe: r.importe_ofrecido != null ? String(r.importe_ofrecido) : '', plazo: r.plazo || '', resumen: r.resumen || '', accion: r.accion_sugerida || '' });
  };
  const guardarEdicion = async (id) => {
    try {
      await extrajudicialAPI.respuestaEditar(reclamacionId, id, {
        clasificacion: editForm.clasificacion,
        importe_ofrecido: editForm.importe !== '' ? parseFloat(editForm.importe) : null,
        plazo: editForm.plazo || null, resumen: editForm.resumen || null, accion_sugerida: editForm.accion || null,
      });
      toast({ title: 'Respuesta actualizada' });
      setEditId(null); await load(); if (typeof onCambio === 'function') onCambio();
    } catch (e) { toast({ title: 'Error', description: err(e, 'No se pudo guardar'), variant: 'destructive' }); }
  };
  const reanalizar = async (id) => {
    setReanalId(id);
    try {
      await extrajudicialAPI.respuestaReanalizar(reclamacionId, id, { provider, model });
      toast({ title: 'Reanalizado con IA' });
      await load(); if (typeof onCambio === 'function') onCambio();
    } catch (e) { toast({ title: 'Error', description: err(e, 'No se pudo reanalizar'), variant: 'destructive' }); }
    finally { setReanalId(null); }
  };
  const eliminar = async (id) => {
    try { await extrajudicialAPI.respuestaEliminar(reclamacionId, id); setRespuestas((p) => p.filter((x) => x.id !== id)); toast({ title: 'Eliminada' }); }
    catch (e) { toast({ title: 'Error', description: err(e, 'No se pudo eliminar'), variant: 'destructive' }); }
  };

  const fmtImporte = (v) => (v === null || v === undefined) ? null : new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v);
  const ClasifSelect = ({ value, onChange, testid }) => (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm bg-white" data-testid={testid}>
      {CLASIF_KEYS.map((k) => <option key={k} value={k}>{CLASIF[k].label}</option>)}
    </select>
  );

  return (
    <Card data-testid="respuestas-banco-imap">
      <CardHeader>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2"><Mail className="w-5 h-5 text-blue-600" /><CardTitle className="text-base">Respuestas del Banco (IMAP + IA)</CardTitle></div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={revisar} disabled={revisando} data-testid="btn-revisar-respuestas">{revisando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}Revisar (IMAP)</Button>
            <Button size="sm" variant="outline" onClick={sincronizar} disabled={sincronizando} data-testid="btn-sincronizar-bandeja">{sincronizando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Inbox className="w-4 h-4 mr-1" />}Sincronizar todas</Button>
            <Button size="sm" onClick={abrirBandeja} data-testid="btn-ver-bandeja"><Mail className="w-4 h-4 mr-1" />Ver bandeja (manual)</Button>
          </div>
        </div>
        <CardDescription>Revisión automática por IA y revisión manual de respaldo (vincular correos y abrir adjuntos).</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="p-3 bg-slate-50 border border-slate-200 rounded-md space-y-2" data-testid="config-cuenta-imap">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-slate-700"><Settings className="w-4 h-4" /><span className="font-medium">Cuenta de correo (Atención Cliente)</span></div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={probarConexion} disabled={probando} data-testid="btn-probar-imap">{probando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <PlugZap className="w-4 h-4 mr-1" />}Probar conexión IMAP</Button>
              <a href="/configuracion/atencion-cliente" data-testid="link-config-atencion"><Button size="sm" variant="secondary"><Settings className="w-4 h-4 mr-1" />Configurar cuenta</Button></a>
            </div>
          </div>
          {estadoImap && (
            <div className={`flex items-start gap-2 text-xs ${estadoImap.ok ? 'text-green-700' : 'text-red-700'}`} data-testid="imap-estado">
              {estadoImap.ok ? <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
              <div><div>{estadoImap.mensaje}</div>{estadoImap.host && <div className="text-slate-500 mt-0.5">Servidor: {estadoImap.host}:{estadoImap.port} · Usuario: {estadoImap.user}</div>}</div>
            </div>
          )}
          <div className="flex items-start gap-2 text-xs text-slate-500"><Info className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>La cuenta <strong>reclamaciones@lexduo.cat</strong> se configura en <strong>Configuración → Atención Cliente</strong> (IMAP por SSL 993).</span></div>
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Proveedor IA</label>
            <select data-testid="resp-provider-select" value={provider} onChange={(e) => onProviderChange(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm bg-white">
              {Object.keys(modelos).length === 0 && <option value="">(por defecto)</option>}
              {Object.keys(modelos).map((p) => <option key={p} value={p}>{p}</option>)}
            </select></div>
          <div><label className="block text-xs font-medium text-gray-600 mb-1">Modelo</label>
            <select data-testid="resp-model-select" value={model} onChange={(e) => setModel(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm bg-white">
              {(modelos[provider] || []).length === 0 && <option value="">(por defecto)</option>}
              {(modelos[provider] || []).map((m) => <option key={m} value={m}>{m}</option>)}
            </select></div>
        </div>

        {respuestas.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm" data-testid="respuestas-vacio"><AlertCircle className="w-8 h-8 mx-auto mb-2 text-gray-300" />Aún no hay respuestas registradas.</div>
        ) : (
          <div className="space-y-3" data-testid="respuestas-lista">
            {respuestas.map((r) => {
              const c = CLASIF[r.clasificacion] || CLASIF.otro;
              const editando = editId === r.id;
              return (
                <div key={r.id} className="border rounded-lg p-3 space-y-1" data-testid={`respuesta-${r.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Badge className={c.cls}>{c.label}</Badge>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-gray-500 mr-1">{r.fecha_email}</span>
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => editando ? setEditId(null) : iniciarEdicion(r)} data-testid={`btn-editar-${r.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2" disabled={reanalId === r.id} onClick={() => reanalizar(r.id)} data-testid={`btn-reanalizar-${r.id}`}>{reanalId === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}</Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600" onClick={() => eliminar(r.id)} data-testid={`btn-eliminar-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                  <div className="text-sm font-medium truncate">{r.asunto || '(sin asunto)'}</div>
                  <div className="text-xs text-gray-500 truncate">De: {r.remitente}</div>
                  {!editando ? (
                    <>
                      {r.resumen && <p className="text-sm text-gray-700 mt-1">{r.resumen}</p>}
                      <div className="flex flex-wrap gap-3 text-xs mt-1">
                        {fmtImporte(r.importe_ofrecido) && <span className="text-green-700 font-semibold">Oferta: {fmtImporte(r.importe_ofrecido)}</span>}
                        {r.plazo && <span className="text-gray-600">Plazo: {r.plazo}</span>}
                      </div>
                      {r.accion_sugerida && <p className="text-xs text-purple-700 mt-1"><strong>Acción sugerida:</strong> {r.accion_sugerida}</p>}
                    </>
                  ) : (
                    <div className="space-y-2 mt-2 bg-gray-50 p-2 rounded" data-testid={`edit-form-${r.id}`}>
                      <div className="flex flex-wrap gap-2 items-center">
                        <ClasifSelect value={editForm.clasificacion} onChange={(v) => setEditForm((f) => ({ ...f, clasificacion: v }))} testid={`edit-clasif-${r.id}`} />
                        <Input className="w-32 h-9" type="number" placeholder="Importe €" value={editForm.importe} onChange={(e) => setEditForm((f) => ({ ...f, importe: e.target.value }))} data-testid={`edit-importe-${r.id}`} />
                        <Input className="w-40 h-9" placeholder="Plazo" value={editForm.plazo} onChange={(e) => setEditForm((f) => ({ ...f, plazo: e.target.value }))} data-testid={`edit-plazo-${r.id}`} />
                      </div>
                      <Textarea rows={2} placeholder="Resumen" value={editForm.resumen} onChange={(e) => setEditForm((f) => ({ ...f, resumen: e.target.value }))} data-testid={`edit-resumen-${r.id}`} />
                      <Textarea rows={2} placeholder="Acción sugerida" value={editForm.accion} onChange={(e) => setEditForm((f) => ({ ...f, accion: e.target.value }))} data-testid={`edit-accion-${r.id}`} />
                      <div className="flex gap-2"><Button size="sm" onClick={() => guardarEdicion(r.id)} data-testid={`btn-guardar-edit-${r.id}`}>Guardar</Button><Button size="sm" variant="outline" onClick={() => setEditId(null)}>Cancelar</Button></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={bandejaOpen} onOpenChange={setBandejaOpen}>
        <DialogContent className="w-[92vw] max-w-[1400px] h-[88vh] max-h-[88vh] overflow-y-auto rounded-lg" data-testid="bandeja-dialog">
          <DialogHeader><DialogTitle>Bandeja de entrada (revisión manual)</DialogTitle></DialogHeader>
          <div className="flex items-end gap-2 flex-wrap mb-3">
            <div><label className="block text-xs font-medium text-gray-600 mb-1">Filtro</label>
              <select value={filtro} onChange={(e) => setFiltro(e.target.value)} className="border rounded-md px-2 py-1.5 text-sm bg-white" data-testid="bandeja-filtro">
                <option value="dias">Últimos 30 días</option>
                <option value="unseen">No leídos</option>
                <option value="todos">Todos (últimos 100)</option>
              </select></div>
            <Button size="sm" variant="outline" onClick={cargarBandeja} disabled={cargandoBandeja} data-testid="btn-cargar-bandeja">{cargandoBandeja ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}Cargar</Button>
          </div>
          {cargandoBandeja ? (
            <div className="text-center py-8 text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
          ) : correos.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">No hay correos para el filtro seleccionado.</div>
          ) : (
            <div className="space-y-2" data-testid="bandeja-lista">
              {correos.map((m, idx) => (
                <div key={idx} className="border rounded-lg p-2" data-testid={`correo-${idx}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{m.asunto || '(sin asunto)'}</div>
                      <div className="text-xs text-gray-500 truncate">{m.remitente} · {m.fecha}</div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => iniciarVinculo(idx)} data-testid={`btn-vincular-${idx}`}><Link2 className="w-4 h-4 mr-1" />Vincular</Button>
                  </div>
                  {m.adjuntos && m.adjuntos.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2" data-testid={`adjuntos-${idx}`}>
                      {m.adjuntos.map((a, i) => {
                        const key = `${m.uid}-${a.index}`;
                        return (
                          <Button key={i} size="sm" variant="outline" className="h-7" disabled={abriendoAdj === key} onClick={() => abrirAdjunto(m.uid, a)} data-testid={`adjunto-${idx}-${i}`}>
                            {abriendoAdj === key ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Paperclip className="w-3 h-3 mr-1" />}{a.filename}
                          </Button>
                        );
                      })}
                    </div>
                  )}
                  <details className="mt-1"><summary className="text-xs text-blue-600 cursor-pointer">Ver cuerpo</summary><pre className="text-xs whitespace-pre-wrap text-gray-700 mt-1 max-h-64 overflow-y-auto bg-gray-50 p-2 rounded">{m.cuerpo}</pre></details>
                  {vincForm && vincForm.idx === idx && (
                    <div className="mt-2 bg-blue-50 p-2 rounded space-y-2" data-testid={`vincular-form-${idx}`}>
                      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={vincForm.usar_ia} onChange={(e) => setVincForm((f) => ({ ...f, usar_ia: e.target.checked }))} data-testid={`vinc-usar-ia-${idx}`} />Clasificar con IA automáticamente</label>
                      {!vincForm.usar_ia && (
                        <>
                          <div className="flex flex-wrap gap-2 items-center">
                            <ClasifSelect value={vincForm.clasificacion} onChange={(v) => setVincForm((f) => ({ ...f, clasificacion: v }))} testid={`vinc-clasif-${idx}`} />
                            <Input className="w-32 h-9" type="number" placeholder="Importe €" value={vincForm.importe} onChange={(e) => setVincForm((f) => ({ ...f, importe: e.target.value }))} data-testid={`vinc-importe-${idx}`} />
                            <Input className="w-40 h-9" placeholder="Plazo" value={vincForm.plazo} onChange={(e) => setVincForm((f) => ({ ...f, plazo: e.target.value }))} data-testid={`vinc-plazo-${idx}`} />
                          </div>
                          <Textarea rows={2} placeholder="Resumen" value={vincForm.resumen} onChange={(e) => setVincForm((f) => ({ ...f, resumen: e.target.value }))} data-testid={`vinc-resumen-${idx}`} />
                        </>
                      )}
                      <div className="flex gap-2"><Button size="sm" onClick={confirmarVinculo} disabled={vinculando} data-testid={`btn-confirmar-vinculo-${idx}`}>{vinculando ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link2 className="w-4 h-4 mr-1" />}Vincular a esta reclamación</Button><Button size="sm" variant="ghost" onClick={() => setVincForm(null)}><X className="w-4 h-4" /></Button></div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RespuestasBancoIMAP;
